import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  Package, ArrowLeftRight, AlarmClock, ClipboardList, ShoppingCart,
  Filter, Search, FileText, FileSpreadsheet, AlertTriangle,
  ChevronLeft, ChevronRight, CalendarCheck
} from 'lucide-react';
import { toast } from 'sonner';
import { downloadPdf } from '../utils/downloadPdf';
import { fmtDate as fmtDateTz, fmtDateTime as fmtDateTimeTz } from '../lib/datetime';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatMoney = (n) =>
  new Intl.NumberFormat('es-AR', { style: 'currency', currency: 'ARS' }).format(Number(n || 0));

const formatDate = (s) => (s ? fmtDateTz(s) : '-');
const formatDateTime = (s) => (s ? fmtDateTimeTz(s) : '-');

// ==== Semana ISO 8601 (lunes-domingo) ====
// IMPORTANTE: todo se calcula en UTC para evitar desfasajes por zona horaria
// (bug detectado en v3.22.4: al pisar los 00:00 UTC con getFullYear/getMonth/getDate
// locales en UTC-3, el domingo se leía como día previo y el shift saltaba de a 2).
function _toUtcMidnight(dt) {
  return new Date(Date.UTC(dt.getFullYear(), dt.getMonth(), dt.getDate()));
}
function _isoWeekAndYearFromUtcMidnight(d) {
  // d ya es UTC 00:00 del día calendario. Recorro al jueves de esa semana ISO.
  const t = new Date(d);
  t.setUTCDate(t.getUTCDate() + 4 - (t.getUTCDay() || 7));
  const yearStart = new Date(Date.UTC(t.getUTCFullYear(), 0, 1));
  const week = Math.ceil(((t - yearStart) / 86400000 + 1) / 7);
  return { year: t.getUTCFullYear(), week };
}
function getIsoYear(dt) { return _isoWeekAndYearFromUtcMidnight(_toUtcMidnight(dt)).year; }
function getIsoWeek(dt) { return _isoWeekAndYearFromUtcMidnight(_toUtcMidnight(dt)).week; }
// Lunes y domingo de una semana ISO (YYYY-MM-DD)
function isoWeekBounds(year, week) {
  // Empezar en el 4 de enero (siempre está en la semana 1 ISO) y desplazarse.
  const jan4 = new Date(Date.UTC(year, 0, 4));
  const jan4Dow = jan4.getUTCDay() || 7;
  const mondayW1 = new Date(jan4);
  mondayW1.setUTCDate(jan4.getUTCDate() - (jan4Dow - 1));
  const monday = new Date(mondayW1);
  monday.setUTCDate(mondayW1.getUTCDate() + (week - 1) * 7);
  const sunday = new Date(monday);
  sunday.setUTCDate(monday.getUTCDate() + 6);
  const fmt = (d) => d.toISOString().slice(0, 10);
  return { monday: fmt(monday), sunday: fmt(sunday) };
}
function shiftIsoWeek(year, week, delta) {
  const { monday } = isoWeekBounds(year, week);
  const [y, m, d] = monday.split('-').map(Number);
  const dt = new Date(Date.UTC(y, m - 1, d));
  dt.setUTCDate(dt.getUTCDate() + delta * 7);
  return _isoWeekAndYearFromUtcMidnight(dt);
}

const IR_STATUS = [
  ['submitted', 'Pendiente'], ['approved', 'Aprobado'], ['partially_approved', 'Aprob. Parcial'],
  ['rejected', 'Rechazado'], ['dispensed', 'Despachado'], ['partially_dispensed', 'Desp. Parcial'],
  ['cancelled', 'Cancelado'], ['returned', 'Devuelto'],
];
const PRIORITIES = [['baja', 'Baja'], ['media', 'Media'], ['alta', 'Alta'], ['urgente', 'Urgente']];
const PO_STATUS = [
  ['generated', 'Generada'], ['received', 'Recibida'], ['received_with_shortage', 'Recibida c/Faltante'], ['partial_received', 'En Revisión'],
  ['rejected', 'Rechazada'], ['cancelled', 'Cancelada'],
];

function Reports() {
  const [searchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState(searchParams.get('tab') || 'stock');
  const [loading, setLoading] = useState(false);

  // Masters
  const [warehouses, setWarehouses] = useState([]);
  const [articles, setArticles] = useState([]);
  const [areas, setAreas] = useState([]);
  const [users, setUsers] = useState([]);

  // Filters comunes
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [warehouseId, setWarehouseId] = useState('');

  // Stock filters
  const [stockSearch, setStockSearch] = useState('');
  const [onlyCritical, setOnlyCritical] = useState(false);
  const [stockData, setStockData] = useState(null);

  // Movements filters
  const [articleId, setArticleId] = useState('');
  const [movType, setMovType] = useState('');
  const [userId, setUserId] = useState('');
  const [movData, setMovData] = useState(null);

  // Expirations filters
  const [daysAhead, setDaysAhead] = useState(90);
  const [includeExpired, setIncludeExpired] = useState(searchParams.get('expired') === '1');
  const [expData, setExpData] = useState(null);

  // Internal requests filters
  const [areaId, setAreaId] = useState('');
  const [irStatus, setIrStatus] = useState('');
  const [irPriority, setIrPriority] = useState('');
  const [irData, setIrData] = useState(null);

  // Purchase orders filters
  const [poSupplier, setPoSupplier] = useState('');
  const [poStatus, setPoStatus] = useState('');
  const [poData, setPoData] = useState(null);

  // Total por Medicamento (REM/OPI) — filtros propios
  const [byArtWh, setByArtWh] = useState('');
  const [byArtFrom, setByArtFrom] = useState('');
  const [byArtTo, setByArtTo] = useState('');
  const [byArtIncludeSupplies, setByArtIncludeSupplies] = useState(false);
  // v3.22.4: switch default ON (devengo semanal). Cuando ON → selector Semana ISO.
  const [byArtAccrual, setByArtAccrual] = useState(true);
  const [byArtIsoWeek, setByArtIsoWeek] = useState(() => getIsoWeek(new Date()));
  const [byArtIsoYear, setByArtIsoYear] = useState(() => getIsoYear(new Date()));
  const [remByArtData, setRemByArtData] = useState(null);
  const [opiByArtData, setOpiByArtData] = useState(null);

  useEffect(() => {
    loadMasters();
  }, []);

  const loadMasters = async () => {
    try {
      const [wRes, aRes, arRes, uRes] = await Promise.all([
        axios.get(`${API}/warehouses`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/articles`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/internal-areas`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API}/users`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setWarehouses(wRes.data || []);
      setArticles(aRes.data || []);
      setAreas(arRes.data || []);
      setUsers(uRes.data || []);
    } catch (err) {
      console.error('Error loading masters', err);
    }
  };

  // ---------- Loaders ----------
  const loadStock = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (warehouseId) p.append('warehouse_id', warehouseId);
      if (onlyCritical) p.append('only_critical', 'true');
      if (stockSearch) p.append('search', stockSearch);
      const res = await axios.get(`${API}/reports/v2/stock?${p}`, { withCredentials: true });
      setStockData(res.data);
    } catch (err) {
      toast.error('Error al cargar reporte de stock');
    } finally { setLoading(false); }
  };

  const loadMovements = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (startDate) p.append('start_date', startDate);
      if (endDate) p.append('end_date', endDate);
      if (warehouseId) p.append('warehouse_id', warehouseId);
      if (articleId) p.append('article_id', articleId);
      if (movType) p.append('type', movType);
      if (userId) p.append('user_id', userId);
      const res = await axios.get(`${API}/reports/v2/movements?${p}`, { withCredentials: true });
      setMovData(res.data);
    } catch (err) {
      toast.error('Error al cargar movimientos');
    } finally { setLoading(false); }
  };

  const loadExpirations = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      p.append('days_ahead', daysAhead);
      if (warehouseId) p.append('warehouse_id', warehouseId);
      if (includeExpired) p.append('include_expired', 'true');
      const res = await axios.get(`${API}/reports/v2/expirations?${p}`, { withCredentials: true });
      setExpData(res.data);
    } catch (err) {
      toast.error('Error al cargar vencimientos');
    } finally { setLoading(false); }
  };

  const loadIR = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (startDate) p.append('start_date', startDate);
      if (endDate) p.append('end_date', endDate);
      if (areaId) p.append('area_id', areaId);
      if (irStatus) p.append('status', irStatus);
      if (irPriority) p.append('priority', irPriority);
      const res = await axios.get(`${API}/reports/v2/internal-requests?${p}`, { withCredentials: true });
      setIrData(res.data);
    } catch (err) {
      toast.error('Error al cargar pedidos internos');
    } finally { setLoading(false); }
  };

  const loadPO = async () => {
    setLoading(true);
    try {
      const p = new URLSearchParams();
      if (startDate) p.append('start_date', startDate);
      if (endDate) p.append('end_date', endDate);
      if (poSupplier) p.append('supplier', poSupplier);
      if (poStatus) p.append('status', poStatus);
      const res = await axios.get(`${API}/reports/v2/purchase-orders?${p}`, { withCredentials: true });
      setPoData(res.data);
    } catch (err) {
      toast.error('Error al cargar órdenes de compra');
    } finally { setLoading(false); }
  };

  const loadByArticle = async (kind) => {
    if (!byArtWh) { toast.error('Elegí un subdepósito'); return; }
    const useAccrual = kind === 'rem' && byArtAccrual;
    if (!useAccrual && (!byArtFrom || !byArtTo)) { toast.error('Rango de fechas obligatorio'); return; }
    setLoading(true);
    try {
      const p = new URLSearchParams({ warehouse_id: byArtWh, include_supplies: byArtIncludeSupplies ? 'true' : 'false' });
      if (useAccrual) {
        p.append('mode', 'accrual');
        p.append('iso_year', String(byArtIsoYear));
        p.append('iso_week', String(byArtIsoWeek));
      } else {
        p.append('mode', 'flat');
        p.append('date_from', byArtFrom);
        p.append('date_to', byArtTo);
      }
      const res = await axios.get(`${API}/reports/${kind}-by-article?${p}`, { withCredentials: true });
      if (kind === 'rem') setRemByArtData(res.data); else setOpiByArtData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al generar reporte');
    } finally { setLoading(false); }
  };

  const exportByArticle = (kind, fmt) => {
    const useAccrual = kind === 'rem' && byArtAccrual;
    if (!byArtWh) { toast.error('Elegí subdepósito'); return; }
    if (!useAccrual && (!byArtFrom || !byArtTo)) { toast.error('Completá subdepósito y rango'); return; }
    const p = new URLSearchParams({ warehouse_id: byArtWh, include_supplies: byArtIncludeSupplies ? 'true' : 'false' });
    if (useAccrual) {
      p.append('mode', 'accrual');
      p.append('iso_year', String(byArtIsoYear));
      p.append('iso_week', String(byArtIsoWeek));
    } else {
      p.append('mode', 'flat');
      p.append('date_from', byArtFrom);
      p.append('date_to', byArtTo);
    }
    const ext = fmt === 'pdf' ? 'pdf' : 'xlsx';
    downloadPdf(`/reports/${kind}-by-article/${fmt}?${p}`, `Total_x_Medicamento_${kind.toUpperCase()}_${Date.now()}.${ext}`,
      fmt === 'pdf' ? 'Generando PDF...' : 'Generando Excel...');
  };

  const handleSearch = () => {
    if (activeTab === 'stock') loadStock();
    else if (activeTab === 'movements') loadMovements();
    else if (activeTab === 'expirations') loadExpirations();
    else if (activeTab === 'internal-requests') loadIR();
    else if (activeTab === 'purchase-orders') loadPO();
  };

  const buildExportParams = () => {
    const p = new URLSearchParams();
    if (activeTab === 'stock') {
      if (warehouseId) p.append('warehouse_id', warehouseId);
      if (onlyCritical) p.append('only_critical', 'true');
      if (stockSearch) p.append('search', stockSearch);
    } else if (activeTab === 'movements') {
      if (startDate) p.append('start_date', startDate);
      if (endDate) p.append('end_date', endDate);
      if (warehouseId) p.append('warehouse_id', warehouseId);
      if (articleId) p.append('article_id', articleId);
      if (movType) p.append('type', movType);
      if (userId) p.append('user_id', userId);
    } else if (activeTab === 'expirations') {
      p.append('days_ahead', daysAhead);
      if (warehouseId) p.append('warehouse_id', warehouseId);
      if (includeExpired) p.append('include_expired', 'true');
    } else if (activeTab === 'internal-requests') {
      if (startDate) p.append('start_date', startDate);
      if (endDate) p.append('end_date', endDate);
      if (areaId) p.append('area_id', areaId);
      if (irStatus) p.append('status', irStatus);
      if (irPriority) p.append('priority', irPriority);
    } else if (activeTab === 'purchase-orders') {
      if (startDate) p.append('start_date', startDate);
      if (endDate) p.append('end_date', endDate);
      if (poSupplier) p.append('supplier', poSupplier);
      if (poStatus) p.append('status', poStatus);
    }
    return p.toString();
  };

  const handleExportPdf = () => {
    const qs = buildExportParams();
    const filename = `reporte_${activeTab}_${Date.now()}.pdf`;
    downloadPdf(`/reports/v2/${activeTab}/pdf?${qs}`, filename, 'Generando PDF...');
  };

  const handleExportExcel = () => {
    const qs = buildExportParams();
    const filename = `reporte_${activeTab}_${Date.now()}.xlsx`;
    downloadPdf(`/reports/v2/${activeTab}/excel?${qs}`, filename, 'Generando Excel...');
  };

  const clearFilters = () => {
    setStartDate(''); setEndDate(''); setWarehouseId('');
    setStockSearch(''); setOnlyCritical(false);
    setArticleId(''); setMovType(''); setUserId('');
    setDaysAhead(90); setIncludeExpired(false);
    setAreaId(''); setIrStatus(''); setIrPriority('');
    setPoSupplier(''); setPoStatus('');
  };

  // Reset data al cambiar de tab para no mostrar info cruzada
  // eslint-disable-next-line react-hooks/exhaustive-deps
  // (no se limpia data automaticamente — el usuario re-consulta)

  return (
    <div className="space-y-6" data-testid="reports-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold tracking-tight mb-2">Reportes</h1>
          <p className="text-muted-foreground">Informes operativos con filtros y exportación PDF / Excel</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={handleExportPdf} disabled={loading} data-testid="export-pdf-btn">
            <FileText className="w-4 h-4 mr-2" />PDF
          </Button>
          <Button variant="outline" onClick={handleExportExcel} disabled={loading} data-testid="export-excel-btn">
            <FileSpreadsheet className="w-4 h-4 mr-2" />Excel
          </Button>
        </div>
      </div>

      {/* Filtros globales (ocultos para reportes con filtros propios) */}
      {activeTab !== 'rem-by-article' && activeTab !== 'opi-by-article' && (
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4" /> Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            {/* Fechas (movs, IR, OC) */}
            {(activeTab === 'movements' || activeTab === 'internal-requests' || activeTab === 'purchase-orders') && (
              <>
                <div>
                  <Label className="text-xs">Desde</Label>
                  <Input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} data-testid="filter-start-date" />
                </div>
                <div>
                  <Label className="text-xs">Hasta</Label>
                  <Input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} data-testid="filter-end-date" />
                </div>
              </>
            )}

            {/* Depósito (stock, movs, exp) */}
            {(activeTab === 'stock' || activeTab === 'movements' || activeTab === 'expirations') && (
              <div>
                <Label className="text-xs">Depósito</Label>
                <select
                  className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm"
                  value={warehouseId}
                  onChange={e => setWarehouseId(e.target.value)}
                  data-testid="filter-warehouse"
                >
                  <option value="">Todos</option>
                  {warehouses.map(w => (
                    <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>
                  ))}
                </select>
              </div>
            )}

            {/* Stock-specific */}
            {activeTab === 'stock' && (
              <>
                <div className="col-span-2">
                  <Label className="text-xs">Buscar artículo</Label>
                  <Input value={stockSearch} onChange={e => setStockSearch(e.target.value)} placeholder="Nombre…" data-testid="filter-stock-search" />
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="filter-only-critical">
                    <input type="checkbox" checked={onlyCritical} onChange={e => setOnlyCritical(e.target.checked)} />
                    Sólo críticos
                  </label>
                </div>
              </>
            )}

            {/* Movements-specific */}
            {activeTab === 'movements' && (
              <>
                <div>
                  <Label className="text-xs">Artículo</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={articleId} onChange={e => setArticleId(e.target.value)} data-testid="filter-article">
                    <option value="">Todos</option>
                    {articles.map(a => <option key={a.article_id} value={a.article_id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Tipo</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={movType} onChange={e => setMovType(e.target.value)} data-testid="filter-mov-type">
                    <option value="">Todos</option>
                    <option value="entry">Entrada</option>
                    <option value="exit">Salida</option>
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Usuario</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={userId} onChange={e => setUserId(e.target.value)} data-testid="filter-user">
                    <option value="">Todos</option>
                    {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
                  </select>
                </div>
              </>
            )}

            {/* Expirations-specific */}
            {activeTab === 'expirations' && (
              <>
                <div>
                  <Label className="text-xs">Próximos días</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={daysAhead} onChange={e => setDaysAhead(Number(e.target.value))} data-testid="filter-days-ahead">
                    <option value={30}>30 días</option>
                    <option value={60}>60 días</option>
                    <option value={90}>90 días</option>
                    <option value={180}>180 días</option>
                    <option value={365}>1 año</option>
                  </select>
                </div>
                <div className="flex items-end">
                  <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="filter-include-expired">
                    <input type="checkbox" checked={includeExpired} onChange={e => setIncludeExpired(e.target.checked)} />
                    Incluir vencidos
                  </label>
                </div>
              </>
            )}

            {/* IR-specific */}
            {activeTab === 'internal-requests' && (
              <>
                <div>
                  <Label className="text-xs">Área</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={areaId} onChange={e => setAreaId(e.target.value)} data-testid="filter-area">
                    <option value="">Todas</option>
                    {areas.map(a => <option key={a.area_id} value={a.area_id}>{a.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Estado</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={irStatus} onChange={e => setIrStatus(e.target.value)} data-testid="filter-ir-status">
                    <option value="">Todos</option>
                    {IR_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
                <div>
                  <Label className="text-xs">Prioridad</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={irPriority} onChange={e => setIrPriority(e.target.value)} data-testid="filter-priority">
                    <option value="">Todas</option>
                    {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </>
            )}

            {/* PO-specific */}
            {activeTab === 'purchase-orders' && (
              <>
                <div>
                  <Label className="text-xs">Proveedor</Label>
                  <Input value={poSupplier} onChange={e => setPoSupplier(e.target.value)} placeholder="Nombre…" data-testid="filter-supplier" />
                </div>
                <div>
                  <Label className="text-xs">Estado</Label>
                  <select className="w-full h-9 rounded-md border border-input bg-background px-3 text-sm" value={poStatus} onChange={e => setPoStatus(e.target.value)} data-testid="filter-po-status">
                    <option value="">Todos</option>
                    {PO_STATUS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
                  </select>
                </div>
              </>
            )}
          </div>
          <div className="flex gap-2 mt-3">
            <Button onClick={handleSearch} disabled={loading} data-testid="apply-filters-btn">
              <Search className="w-4 h-4 mr-2" />{loading ? 'Cargando…' : 'Consultar'}
            </Button>
            <Button variant="ghost" onClick={clearFilters} data-testid="clear-filters-btn">Limpiar Filtros</Button>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-7">
          <TabsTrigger value="stock" data-testid="tab-stock" className="flex items-center gap-1">
            <Package className="w-4 h-4" />Stock
          </TabsTrigger>
          <TabsTrigger value="movements" data-testid="tab-movements" className="flex items-center gap-1">
            <ArrowLeftRight className="w-4 h-4" />Movimientos
          </TabsTrigger>
          <TabsTrigger value="expirations" data-testid="tab-expirations" className="flex items-center gap-1">
            <AlarmClock className="w-4 h-4" />Vencimientos
          </TabsTrigger>
          <TabsTrigger value="internal-requests" data-testid="tab-internal-requests" className="flex items-center gap-1">
            <ClipboardList className="w-4 h-4" />Pedidos Internos
          </TabsTrigger>
          <TabsTrigger value="purchase-orders" data-testid="tab-purchase-orders" className="flex items-center gap-1">
            <ShoppingCart className="w-4 h-4" />Órdenes de Compra
          </TabsTrigger>
          <TabsTrigger value="rem-by-article" data-testid="tab-rem-by-article" className="flex items-center gap-1">
            <FileText className="w-4 h-4" />Total x Med. (REM)
          </TabsTrigger>
          <TabsTrigger value="opi-by-article" data-testid="tab-opi-by-article" className="flex items-center gap-1">
            <FileText className="w-4 h-4" />Total x Med. (OPI)
          </TabsTrigger>
        </TabsList>

        {/* STOCK */}
        <TabsContent value="stock" className="mt-4">
          {!stockData ? <EmptyState icon={<Package className="w-12 h-12 mx-auto mb-4 opacity-30" />} text="Presione Consultar para ver el reporte de Stock" />
            : <StockReport data={stockData} />}
        </TabsContent>

        {/* MOVEMENTS */}
        <TabsContent value="movements" className="mt-4">
          {!movData ? <EmptyState icon={<ArrowLeftRight className="w-12 h-12 mx-auto mb-4 opacity-30" />} text="Presione Consultar para ver el reporte de Movimientos" />
            : <MovementsReport data={movData} />}
        </TabsContent>

        {/* EXPIRATIONS */}
        <TabsContent value="expirations" className="mt-4">
          {!expData ? <EmptyState icon={<AlarmClock className="w-12 h-12 mx-auto mb-4 opacity-30" />} text="Presione Consultar para ver los Vencimientos próximos" />
            : <ExpirationsReport data={expData} />}
        </TabsContent>

        {/* IR */}
        <TabsContent value="internal-requests" className="mt-4">
          {!irData ? <EmptyState icon={<ClipboardList className="w-12 h-12 mx-auto mb-4 opacity-30" />} text="Presione Consultar para ver Pedidos Internos" />
            : <InternalRequestsReport data={irData} />}
        </TabsContent>

        {/* PO */}
        <TabsContent value="purchase-orders" className="mt-4">
          {!poData ? <EmptyState icon={<ShoppingCart className="w-12 h-12 mx-auto mb-4 opacity-30" />} text="Presione Consultar para ver Órdenes de Compra" />
            : <PurchaseOrdersReport data={poData} />}
        </TabsContent>

        {/* TOTAL x MEDICAMENTO - REM */}
        <TabsContent value="rem-by-article" className="mt-4">
          <ByArticleReport
            kind="rem"
            data={remByArtData}
            warehouses={warehouses}
            whId={byArtWh} setWhId={setByArtWh}
            dateFrom={byArtFrom} setDateFrom={setByArtFrom}
            dateTo={byArtTo} setDateTo={setByArtTo}
            includeSupplies={byArtIncludeSupplies} setIncludeSupplies={setByArtIncludeSupplies}
            accrual={byArtAccrual} setAccrual={setByArtAccrual}
            isoYear={byArtIsoYear} setIsoYear={setByArtIsoYear}
            isoWeek={byArtIsoWeek} setIsoWeek={setByArtIsoWeek}
            onSearch={() => loadByArticle('rem')}
            onPdf={() => exportByArticle('rem', 'pdf')}
            onExcel={() => exportByArticle('rem', 'excel')}
            loading={loading}
          />
        </TabsContent>

        {/* TOTAL x MEDICAMENTO - OPI */}
        <TabsContent value="opi-by-article" className="mt-4">
          <ByArticleReport
            kind="opi"
            data={opiByArtData}
            warehouses={warehouses}
            whId={byArtWh} setWhId={setByArtWh}
            dateFrom={byArtFrom} setDateFrom={setByArtFrom}
            dateTo={byArtTo} setDateTo={setByArtTo}
            includeSupplies={byArtIncludeSupplies} setIncludeSupplies={setByArtIncludeSupplies}
            onSearch={() => loadByArticle('opi')}
            onPdf={() => exportByArticle('opi', 'pdf')}
            onExcel={() => exportByArticle('opi', 'excel')}
            loading={loading}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}

const ByArticleReport = ({ kind, data, warehouses, whId, setWhId, dateFrom, setDateFrom, dateTo, setDateTo, includeSupplies, setIncludeSupplies, accrual, setAccrual, isoYear, setIsoYear, isoWeek, setIsoWeek, onSearch, onPdf, onExcel, loading }) => {
  const isRem = kind === 'rem';
  const isAccrualMode = isRem && !!accrual;
  const showImputationCol = isRem && !!data?.with_imputation;
  const impLabel = data?.mode === 'accrual' ? 'Devengo semana' : 'Imputación';
  const title = isRem
    ? (data?.mode === 'accrual' ? 'Total por Medicamento — REM Devengado' : 'Total por Medicamento — REM Recibidas')
    : 'Total por Medicamento — OPI Recibidas (despachos)';
  const helper = isRem
    ? (isAccrualMode
        ? 'Devengo semanal: cada REM se imputa en 4 semanas ISO (¼ por semana). Se muestra la cuota que corresponde a la semana elegida.'
        : 'Suma las unidades ingresadas al subdepósito por familiares (REM) en el rango.')
    : 'Suma las unidades entregadas al subdepósito por despacho de Pedidos Internos (OPI) en el rango. Incluye despachos parciales.';
  const weekRange = (isRem && isoYear && isoWeek) ? isoWeekBoundsFmt(isoYear, isoWeek) : null;
  const goPrev = () => { const s = shiftIsoWeek(isoYear, isoWeek, -1); setIsoYear(s.year); setIsoWeek(s.week); };
  const goNext = () => { const s = shiftIsoWeek(isoYear, isoWeek, 1); setIsoYear(s.year); setIsoWeek(s.week); };
  const goToday = () => { const now = new Date(); setIsoYear(getIsoYear(now)); setIsoWeek(getIsoWeek(now)); };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div>
              <Label>Subdepósito *</Label>
              <select value={whId} onChange={(e) => setWhId(e.target.value)}
                className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
                data-testid={`${kind}-by-art-wh`}>
                <option value="">Elegí subdepósito...</option>
                {warehouses.map(w => <option key={w.warehouse_id} value={w.warehouse_id}>{w.name}</option>)}
              </select>
            </div>
            {isAccrualMode ? (
              <div className="md:col-span-2">
                <Label>Semana ISO (lunes → domingo) *</Label>
                <div className="flex items-center gap-1">
                  <Button variant="outline" size="icon" onClick={goPrev} title="Semana anterior" data-testid={`${kind}-by-art-week-prev`}>
                    <ChevronLeft className="w-4 h-4" />
                  </Button>
                  <div className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm flex items-center justify-center gap-2 font-medium" data-testid={`${kind}-by-art-week-label`}>
                    <span className="text-violet-700">{isoWeek}/{isoYear}</span>
                    {weekRange && <span className="text-xs text-muted-foreground">· {weekRange}</span>}
                  </div>
                  <Button variant="outline" size="icon" onClick={goNext} title="Semana siguiente" data-testid={`${kind}-by-art-week-next`}>
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={goToday} title="Semana en curso" data-testid={`${kind}-by-art-week-today`}>
                    <CalendarCheck className="w-4 h-4 mr-1" /> Hoy
                  </Button>
                </div>
              </div>
            ) : (
              <>
                <div>
                  <Label>Desde *</Label>
                  <Input type="date" value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} data-testid={`${kind}-by-art-from`} />
                </div>
                <div>
                  <Label>Hasta *</Label>
                  <Input type="date" value={dateTo} onChange={(e) => setDateTo(e.target.value)} data-testid={`${kind}-by-art-to`} />
                </div>
              </>
            )}
            <div className="flex items-end gap-2">
              <Button onClick={onSearch} disabled={loading} className="flex-1" data-testid={`${kind}-by-art-search`}>
                <Search className="w-4 h-4 mr-1" /> Consultar
              </Button>
            </div>
          </div>
          <div className="flex items-center gap-2 mt-3">
            <input
              type="checkbox"
              id={`${kind}-inc-supplies`}
              checked={includeSupplies}
              onChange={(e) => setIncludeSupplies(e.target.checked)}
              data-testid={`${kind}-by-art-include-supplies`}
              className="w-4 h-4 cursor-pointer"
            />
            <label htmlFor={`${kind}-inc-supplies`} className="text-sm cursor-pointer select-none">
              Incluir insumos (agua oxigenada, gasas, guantes, agujas, etc.)
            </label>
            <span className="ml-2 text-xs text-muted-foreground">
              {includeSupplies ? '· Alcance: Medicamentos + Insumos' : '· Alcance: solo Medicamentos'}
            </span>
          </div>
          {isRem && setAccrual && (
            <div className="flex items-center gap-2 mt-3 p-2 rounded-md border border-violet-200 bg-violet-50/50">
              <input
                type="checkbox"
                id={`${kind}-accrual`}
                checked={!!accrual}
                onChange={(e) => setAccrual(e.target.checked)}
                data-testid={`${kind}-by-art-accrual`}
                className="w-4 h-4 cursor-pointer accent-violet-600"
              />
              <label htmlFor={`${kind}-accrual`} className="text-sm cursor-pointer select-none font-medium text-violet-900">
                CON IMPUTACIÓN DEVENGADA
              </label>
              <span className="ml-2 text-xs text-violet-700/80">
                · Devengo semanal ¼ por semana ISO (lun→dom), 4 semanas por REM. Al desactivar, vuelve al modo por rango libre sin imputación.
              </span>
            </div>
          )}
          <p className="text-xs text-muted-foreground mt-2">{helper}</p>
          {data && (
            <div className="flex gap-2 mt-4 justify-end">
              <Button variant="outline" onClick={onPdf} data-testid={`${kind}-by-art-pdf`}>
                <FileText className="w-4 h-4 mr-1" /> PDF
              </Button>
              <Button variant="outline" onClick={onExcel} data-testid={`${kind}-by-art-excel`}>
                <FileSpreadsheet className="w-4 h-4 mr-1" /> Excel
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {!data ? (
        <EmptyState icon={<FileText className="w-12 h-12 mx-auto mb-4 opacity-30" />} text={`Elegí subdepósito y ${isAccrualMode ? 'semana' : 'rango'}, luego "Consultar" para ver el reporte`} />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">{title}</CardTitle>
            <div className="text-sm text-muted-foreground">
              <strong>Subdepósito:</strong> {data.warehouse_name} · <strong>Período:</strong> {data.period_label}
              {showImputationCol && <> · <strong>{impLabel}:</strong> {data?.mode === 'accrual' ? '¼ semanal (4 semanas por REM)' : '¼ del total del rango'}</>}
            </div>
          </CardHeader>
          <CardContent>
            <div className={`grid gap-3 mb-4 ${showImputationCol ? 'grid-cols-4' : 'grid-cols-3'}`}>
              <Card><CardContent className="pt-4 text-center">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Medicamentos</div>
                <div className="text-2xl font-bold mt-1">{data.total_articles}</div>
              </CardContent></Card>
              <Card><CardContent className="pt-4 text-center">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Unidades REM</div>
                <div className="text-2xl font-bold mt-1 text-emerald-600">{data.total_units.toLocaleString('es-AR')}</div>
              </CardContent></Card>
              {showImputationCol && (
                <Card><CardContent className="pt-4 text-center">
                  <div className="text-xs text-muted-foreground uppercase tracking-wide">{impLabel}</div>
                  <div className="text-2xl font-bold mt-1 text-violet-600">{Number(data.total_imputation || 0).toLocaleString('es-AR')}</div>
                </CardContent></Card>
              )}
              <Card><CardContent className="pt-4 text-center">
                <div className="text-xs text-muted-foreground uppercase tracking-wide">Movimientos</div>
                <div className="text-2xl font-bold mt-1">{data.total_movements}</div>
              </CardContent></Card>
            </div>
            <Table>
              <TableHeader><TableRow>
                <TableHead className="w-12">#</TableHead>
                <TableHead>Medicamento</TableHead>
                {!isRem && <TableHead>Presentación</TableHead>}
                <TableHead className="text-right">Unidades</TableHead>
                {showImputationCol && <TableHead className="text-right w-32 text-violet-700">{impLabel}</TableHead>}
                <TableHead className="text-right w-24">Movs.</TableHead>
              </TableRow></TableHeader>
              <TableBody>
                {data.items.length === 0 ? (
                  <TableRow><TableCell colSpan={isRem ? (showImputationCol ? 5 : 4) : 5} className="text-center text-muted-foreground">Sin movimientos en el período</TableCell></TableRow>
                ) : data.items.map((it, i) => (
                  <TableRow key={i}>
                    <TableCell>{i + 1}</TableCell>
                    <TableCell className="font-medium">{it.article_name}</TableCell>
                    {!isRem && <TableCell className="text-sm text-muted-foreground">{it.presentation || '—'}</TableCell>}
                    <TableCell className="text-right font-mono">{Number(it.total_quantity).toLocaleString('es-AR')}</TableCell>
                    {showImputationCol && <TableCell className="text-right font-mono text-violet-700 font-semibold">{Number(it.imputation || 0).toLocaleString('es-AR')}</TableCell>}
                    <TableCell className="text-right">{it.movements_count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

// Helper visual: "07/09 → 13/09/2026"
function isoWeekBoundsFmt(year, week) {
  const { monday, sunday } = isoWeekBounds(year, week);
  const [ym, mm, dm] = monday.split('-');
  const [ys, ms, ds] = sunday.split('-');
  return `${dm}/${mm} → ${ds}/${ms}/${ys}`;
}

const EmptyState = ({ icon, text }) => (
  <Card>
    <CardContent className="py-12 text-center text-muted-foreground">
      {icon}
      <p>{text}</p>
    </CardContent>
  </Card>
);

// =================== SUB-VIEWS ===================
const KpiCard = ({ label, value, color }) => (
  <Card>
    <CardContent className="pt-4 pb-4 text-center">
      <p className="text-xs text-muted-foreground uppercase">{label}</p>
      <p className={`text-xl font-bold ${color || ''}`}>{value}</p>
    </CardContent>
  </Card>
);

const StockReport = ({ data }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KpiCard label="Artículos" value={data.totals.articles} />
      <KpiCard label="Unidades" value={data.totals.total_units.toLocaleString('es-AR')} />
      <KpiCard label="Críticos" value={data.totals.critical_count} color="text-red-600" />
      <KpiCard label="Valorización" value={formatMoney(data.totals.total_value)} color="text-green-700" />
    </div>
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Detalle ({data.rows.length})</CardTitle></CardHeader>
      <CardContent>
        <div className="max-h-[520px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Artículo</TableHead><TableHead>Depósito</TableHead>
                <TableHead className="text-center">Stock</TableHead>
                <TableHead className="text-center">Lotes</TableHead>
                <TableHead className="text-center">P.Rep.</TableHead>
                <TableHead className="text-right">Costo</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead>Categoría</TableHead>
                <TableHead className="text-center">Vence</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r, i) => (
                <TableRow key={r.article_id + i}>
                  <TableCell className="text-sm">{r.name}</TableCell>
                  <TableCell className="text-sm">{r.warehouse_name}</TableCell>
                  <TableCell className={`text-center font-mono ${r.is_critical ? 'text-red-600 font-bold' : ''}`}>
                    {r.stock}{r.is_critical && <AlertTriangle className="inline w-3 h-3 ml-1" />}
                  </TableCell>
                  <TableCell className="text-center font-mono text-muted-foreground">{r.lots_count ?? '-'}</TableCell>
                  <TableCell className="text-center font-mono">{r.reorder_point}</TableCell>
                  <TableCell className="text-right font-mono">{formatMoney(r.current_cost_price)}</TableCell>
                  <TableCell className="text-right font-mono">{formatMoney(r.stock_value)}</TableCell>
                  <TableCell className="text-xs">{r.category}</TableCell>
                  <TableCell className="text-center text-xs">{r.expiration_date ? formatDate(r.expiration_date) : '-'}</TableCell>
                </TableRow>
              ))}
              {data.rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Sin datos</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  </div>
);

const MovementsReport = ({ data }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KpiCard label="Movimientos" value={data.totals.count} />
      <KpiCard label="Entradas" value={data.totals.entries.toLocaleString('es-AR')} color="text-green-600" />
      <KpiCard label="Salidas" value={data.totals.exits.toLocaleString('es-AR')} color="text-red-600" />
      <KpiCard label="Neto" value={data.totals.net.toLocaleString('es-AR')} />
    </div>
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Detalle ({data.rows.length})</CardTitle></CardHeader>
      <CardContent>
        <div className="max-h-[520px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Fecha</TableHead><TableHead>Comprobante</TableHead>
                <TableHead>Artículo</TableHead><TableHead>Depósito</TableHead>
                <TableHead className="text-center">Tipo</TableHead>
                <TableHead className="text-center">Cant.</TableHead>
                <TableHead>Destinatario</TableHead><TableHead>Usuario</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.movement_id}>
                  <TableCell className="text-xs whitespace-nowrap">{formatDateTime(r.created_at)}</TableCell>
                  <TableCell className="font-mono text-xs">{r.voucher_number}</TableCell>
                  <TableCell className="text-sm">{r.article_name}</TableCell>
                  <TableCell className="text-sm">{r.warehouse_name}</TableCell>
                  <TableCell className="text-center">
                    <Badge variant={r.type === 'entry' ? 'default' : 'secondary'} className={r.type === 'entry' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}>
                      {r.type_label}
                    </Badge>
                  </TableCell>
                  <TableCell className="text-center font-mono">{r.quantity}</TableCell>
                  <TableCell className="text-xs">{r.destinatario}</TableCell>
                  <TableCell className="text-xs">{r.user_name}</TableCell>
                </TableRow>
              ))}
              {data.rows.length === 0 && (
                <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Sin movimientos para el período</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  </div>
);

const ExpirationsReport = ({ data }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <KpiCard label="Lotes" value={data.totals.lots} />
      <KpiCard label="Vencidos" value={data.totals.expired_lots} color="text-red-600" />
      <KpiCard label="≤30 días" value={data.totals.soon_lots} color="text-amber-600" />
      <KpiCard label="Unidades" value={data.totals.total_units.toLocaleString('es-AR')} />
      <KpiCard label="Valor riesgo" value={formatMoney(data.totals.total_value)} color="text-red-700" />
    </div>
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Lotes ({data.rows.length})</CardTitle></CardHeader>
      <CardContent>
        <div className="max-h-[520px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Artículo</TableHead><TableHead>Lote</TableHead><TableHead>Depósito</TableHead>
                <TableHead className="text-center">Vence</TableHead>
                <TableHead className="text-center">Días</TableHead>
                <TableHead className="text-center">Cant.</TableHead>
                <TableHead className="text-right">Costo</TableHead>
                <TableHead className="text-right">Valor</TableHead>
                <TableHead>Residente (REM)</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => {
                const tone = r.is_expired ? 'text-red-600 font-bold' : r.days_left <= 30 ? 'text-amber-600 font-bold' : '';
                return (
                  <TableRow key={r.lot_id}>
                    <TableCell className="text-sm">
                      {r.article_name}
                      {r.is_external && <Badge variant="outline" className="ml-2 text-xs">EXT</Badge>}
                    </TableCell>
                    <TableCell className="font-mono text-xs">{r.lot_number}</TableCell>
                    <TableCell className="text-sm">{r.warehouse_name}</TableCell>
                    <TableCell className={`text-center text-xs ${tone}`}>{formatDate(r.expiration_date)}</TableCell>
                    <TableCell className={`text-center font-mono ${tone}`}>{r.days_left}</TableCell>
                    <TableCell className="text-center font-mono">{r.quantity_remaining}</TableCell>
                    <TableCell className="text-right font-mono">{formatMoney(r.cost_price)}</TableCell>
                    <TableCell className="text-right font-mono">{formatMoney(r.total_value)}</TableCell>
                    <TableCell className="text-xs">{r.patient_name || '-'}</TableCell>
                  </TableRow>
                );
              })}
              {data.rows.length === 0 && (
                <TableRow><TableCell colSpan={9} className="text-center py-8 text-muted-foreground">Sin lotes próximos a vencer</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  </div>
);

const InternalRequestsReport = ({ data }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
      <KpiCard label="Pedidos" value={data.totals.count} />
      <KpiCard label="Con faltante" value={data.totals.with_shortage} color="text-red-600" />
      <KpiCard label="Cant. pedida" value={data.totals.total_qty_requested.toLocaleString('es-AR')} />
      <KpiCard label="Cant. despachada" value={data.totals.total_qty_dispensed.toLocaleString('es-AR')} color="text-green-600" />
      <KpiCard label="Media aprob. (h)" value={data.totals.avg_approval_hours} />
    </div>
    <Card>
      <CardHeader className="pb-2"><CardTitle className="text-base">Detalle ({data.rows.length})</CardTitle></CardHeader>
      <CardContent>
        <div className="max-h-[520px] overflow-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nº</TableHead><TableHead>Fecha</TableHead><TableHead>Área</TableHead>
                <TableHead className="text-center">Prio.</TableHead><TableHead className="text-center">Estado</TableHead>
                <TableHead className="text-center">Items</TableHead>
                <TableHead className="text-center">Pedido</TableHead>
                <TableHead className="text-center">Aprob.</TableHead>
                <TableHead className="text-center">Desp.</TableHead>
                <TableHead className="text-center">Falt.</TableHead>
                <TableHead className="text-center">h aprob.</TableHead>
                <TableHead>Solicitante</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.rows.map((r) => (
                <TableRow key={r.request_id}>
                  <TableCell className="font-mono text-xs">{r.request_number}</TableCell>
                  <TableCell className="text-xs">{formatDate(r.created_at)}</TableCell>
                  <TableCell className="text-sm">{r.area_name}</TableCell>
                  <TableCell className="text-center"><Badge variant="outline" className="text-xs">{r.priority_label}</Badge></TableCell>
                  <TableCell className="text-center"><Badge variant="outline" className="text-xs">{r.status_label}</Badge></TableCell>
                  <TableCell className="text-center font-mono">{r.items_count}</TableCell>
                  <TableCell className="text-center font-mono">{r.qty_requested}</TableCell>
                  <TableCell className="text-center font-mono">{r.qty_approved}</TableCell>
                  <TableCell className="text-center font-mono text-green-700">{r.qty_dispensed}</TableCell>
                  <TableCell className={`text-center font-mono ${r.shortage_total ? 'text-red-600 font-bold' : ''}`}>{r.shortage_total}</TableCell>
                  <TableCell className="text-center text-xs">{r.approval_lead_hours ?? '-'}</TableCell>
                  <TableCell className="text-xs">{r.created_by_name}</TableCell>
                </TableRow>
              ))}
              {data.rows.length === 0 && (
                <TableRow><TableCell colSpan={12} className="text-center py-8 text-muted-foreground">Sin pedidos para el período</TableCell></TableRow>
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  </div>
);

const PurchaseOrdersReport = ({ data }) => (
  <div className="space-y-4">
    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
      <KpiCard label="Órdenes" value={data.totals.count} />
      <KpiCard label="Cant. total" value={data.totals.total_qty.toLocaleString('es-AR')} />
      <KpiCard label="Presupuesto" value={formatMoney(data.totals.total_budget)} />
      <KpiCard label="Monto total" value={formatMoney(data.totals.total_amount)} color="text-blue-700" />
    </div>

    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-2">
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Detalle ({data.rows.length})</CardTitle></CardHeader>
          <CardContent>
            <div className="max-h-[500px] overflow-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>OC</TableHead><TableHead>NP</TableHead><TableHead>Fecha</TableHead>
                    <TableHead>Proveedor</TableHead><TableHead className="text-center">Estado</TableHead>
                    <TableHead className="text-center">Items</TableHead>
                    <TableHead className="text-right">Presup.</TableHead>
                    <TableHead className="text-right">Monto</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.rows.map((r) => (
                    <TableRow key={r.order_id}>
                      <TableCell className="font-mono text-xs">{r.order_number}</TableCell>
                      <TableCell className="font-mono text-xs">{r.request_number}</TableCell>
                      <TableCell className="text-xs">{formatDate(r.created_at)}</TableCell>
                      <TableCell className="text-sm">{r.supplier}</TableCell>
                      <TableCell className="text-center"><Badge variant="outline" className="text-xs">{r.status_label}</Badge></TableCell>
                      <TableCell className="text-center font-mono">{r.items_count}</TableCell>
                      <TableCell className="text-right font-mono">{formatMoney(r.budget)}</TableCell>
                      <TableCell className="text-right font-mono font-bold">{formatMoney(r.amount_total)}</TableCell>
                    </TableRow>
                  ))}
                  {data.rows.length === 0 && (
                    <TableRow><TableCell colSpan={8} className="text-center py-8 text-muted-foreground">Sin OC para el período</TableCell></TableRow>
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
      <div>
        <Card>
          <CardHeader className="pb-2"><CardTitle className="text-base">Por Proveedor</CardTitle></CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Proveedor</TableHead>
                  <TableHead className="text-center">OC</TableHead>
                  <TableHead className="text-right">Monto</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(data.by_supplier || []).map((s, i) => (
                  <TableRow key={i}>
                    <TableCell className="text-sm">{s.supplier}</TableCell>
                    <TableCell className="text-center font-mono">{s.orders}</TableCell>
                    <TableCell className="text-right font-mono">{formatMoney(s.amount)}</TableCell>
                  </TableRow>
                ))}
                {(data.by_supplier || []).length === 0 && (
                  <TableRow><TableCell colSpan={3} className="text-center text-muted-foreground py-4">Sin datos</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>
  </div>
);

export default Reports;
